/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { type ReactNode, useState } from 'react'

import { Alert, AssetDetailModal, SimpleTable, TableSkeleton, type AddressCell } from '@/components/ui'
import { useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { formatDistance } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'
import { formatDurationSeconds, formatTripDate } from '@/utils/formatDate'

export interface TripActivityParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

export interface TripActivityRecord {
  assetId: number
  assetName: string
  departTime: Date | string | null
  arrivalTime: Date | string | null
  startGeofence: string | null
  startAddress: string | null
  arrivalGeofence: string | null
  arrivalAddress: string | null
  startLatitude: number | null
  startLongitude: number | null
  arrivalLatitude: number | null
  arrivalLongitude: number | null
  activeSeconds: number
  restSeconds: number | null
  distanceKm: number | null
  totalDistanceKm: number
  totalTripSeconds: number
  durationMinutes: number
}

export interface TripActivityResponse {
  data: TripActivityRecord[]
  count: number
}

export const reportPath = '/insights/reports/trip-activity'

export const TRIP_ACTIVITY_HEADERS = [
  'Asset Name',
  'Depart Time',
  'Arrival Time',
  'Start Geofence',
  'Start Address',
  'Arrival Geofence',
  'Arrival Address',
  'Active Hours',
  'Rest Hours',
  'Distance Travelled',
  'Total Distance Travelled',
  'Total Trip Time',
  'Duration in Minutes',
] as const

const sum = (records: TripActivityRecord[], field: keyof Pick<TripActivityRecord, 'activeSeconds' | 'restSeconds'>) =>
  records.reduce((total, row) => total + (Number.isFinite(row[field]) ? Number(row[field]) : 0), 0)

const TripActivity: InsightDefinition<TripActivityParams, TripActivityRecord[]> = {
  active: true,
  slug: 'report/trip-activity',
  category: 'report',
  name: 'Trip Activity',
  description: 'Shows trip activity for a selected asset over the chosen date range.',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'companyId') {
        return { ...parameters, assetId: undefined }
      }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'assetId',
        control: 'AssetDropdownSingle',
        required: true,
        assetType: AssetTypeFilter.InTransit,
      },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom.map((option) =>
          option.value === 'today' ? { ...option, isDefault: true } : option
        ),
      },
      {
        id: 'dateRange',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (parameters: TripActivityParams) => {
      if (!parameters.companyId || !parameters.assetId) {
        return 'Company and Asset are required.'
      }

      if (parameters.date === 'custom') {
        if (!parameters.from || !parameters.to) {
          return 'Enter the custom date range.'
        }
        const fromDate = new Date(parameters.from)
        const toDate = new Date(parameters.to)
        if (fromDate > toDate) {
          return 'Start date must be before end date'
        }
        const dateValidation = validateDateRangeWithin13Months(parameters.from, parameters.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: TripActivityParams) => {
    const response = await api.get<TripActivityRecord[]>({
      path: `${reportPath}?${buildQuery(parameters)}`,
      origin: 'TripActivity',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: TripActivityParams, title: string) => {
    await downloadCsv(api, reportPath, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const parameters = configuration.parameters as TripActivityParams
    const units = (useDashboard().configuration.units as 'Imperial' | 'Metric') ?? 'Metric'

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={TRIP_ACTIVITY_HEADERS.length} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data) || !data?.length) {
      return null
    }

    const lastRecord = data[data.length - 1]
    const body: Record<string, ReactNode | AddressCell>[] = data.map((row) => ({
      'Asset Name': (
        <button
          type="button"
          onClick={() => setSelectedAsset(row.assetId)}
          className="text-left block text-blue-500 cursor-pointer"
        >
          {row.assetName}
        </button>
      ),
      'Depart Time': formatTripDate(row.departTime),
      'Arrival Time': formatTripDate(row.arrivalTime),
      'Start Geofence': row.startGeofence ?? '',
      'Start Address': genericLocationDrilldown({
        configuration,
        address: row.startAddress ?? undefined,
        latitude: row.startLatitude ?? undefined,
        longitude: row.startLongitude ?? undefined,
        companyId: parameters.companyId,
        markerFields: {
          Asset: row.assetName,
          Time: formatTripDate(row.departTime),
        },
      }),
      'Arrival Geofence': row.arrivalGeofence ?? '',
      'Arrival Address': genericLocationDrilldown({
        configuration,
        address: row.arrivalAddress ?? undefined,
        latitude: row.arrivalLatitude ?? undefined,
        longitude: row.arrivalLongitude ?? undefined,
        companyId: parameters.companyId,
        markerFields: {
          Asset: row.assetName,
          Time: formatTripDate(row.arrivalTime),
        },
      }),
      'Active Hours': formatDurationSeconds(row.activeSeconds),
      'Rest Hours': row.restSeconds === null ? '' : formatDurationSeconds(row.restSeconds),
      'Distance Travelled': formatDistance(row.distanceKm, units),
      'Total Distance Travelled': formatDistance(row.totalDistanceKm, units),
      'Total Trip Time': formatDurationSeconds(row.totalTripSeconds),
      'Duration in Minutes': row.durationMinutes,
    }))

    body.push({
      'Asset Name': '',
      'Depart Time': <strong>Totals</strong>,
      'Arrival Time': '',
      'Start Geofence': '',
      'Start Address': '',
      'Arrival Geofence': '',
      'Arrival Address': '',
      'Active Hours': <strong>{formatDurationSeconds(sum(data, 'activeSeconds'))}</strong>,
      'Rest Hours': <strong>{formatDurationSeconds(sum(data, 'restSeconds'))}</strong>,
      'Distance Travelled': '',
      'Total Distance Travelled': <strong>{formatDistance(lastRecord.totalDistanceKm, units)}</strong>,
      'Total Trip Time': <strong>{formatDurationSeconds(lastRecord.totalTripSeconds)}</strong>,
      'Duration in Minutes': '',
    })

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable
            headers={[...TRIP_ACTIVITY_HEADERS]}
            body={body}
            cellClassNames={{ 'Depart Time': 'text-nowrap', 'Arrival Time': 'text-nowrap' }}
          />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    const parameters = configuration.parameters as TripActivityParams
    if (status.code === InsightStatusCode.ERROR) return ''

    const fields: Record<string, string> = {}
    const company = companies.find((item) => item.id === parameters.companyId)
    if (company) fields.Company = company.name
    if (parameters.assetId) fields.Asset = parameters.assetId
    addDateToCaptionFields({ ...parameters, fields, finite: true })
    return Object.keys(fields).length ? <Caption fields={fields} /> : ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA || !data?.length) {
      return <div>No records found</div>
    }
    return (
      <div>
        {data.length} row{data.length === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default TripActivity
